import React, { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControlLabel,
    Switch,
} from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { Conditional } from '@klp/conditional';
import { ContractManagementRoutes } from '@klp/contractmanagement';
import { useAppParams } from '@klp/core/app';
import { AppRoutes } from '@klp/core/app/config';
import { Block, Heading, Link, Typography } from '@klp/design';
import { Amount } from '@klp/design/Amount';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Loader } from '@klp/design/Loader';
import type { ITableConsumerState } from '@klp/design/Table';
import { Table, TableColumnDef } from '@klp/design/Table';
import { useKlpStyles } from '@klp/design/Theme';
import { ErrorNotification } from '@klp/errorNotification';
import { getDefaultLanguage } from '@klp/layout/Sidebar/config';

import type { ByggKontraktItem } from '../classes';
import { useGetByggKontraktQuery } from '../rtkApi';

import { ByggKontraktSummary } from './ByggKontraktSummary';

const styles = (theme: Theme) => ({
    Accordion: {
        borderRadius: 0,
        borderBottom: '2px solid transparent',
        borderBottomColor: theme.palette.primary.mediumLight,
        '&:before': {
            display: 'none',
        },
        '&:first-child': {
            borderTop: '2px solid transparent',
            borderTopColor: theme.palette.primary.mediumLight,
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
        },
        '&:last-child': {
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
        },
        '& .MuiCollapse-entered': {
            marginBottom: theme.spacing(4),
        },
    },
    AccordionSummary: {
        padding: 0,
    },
    AccordionDetails: {
        '& > *': {
            width: '100%',
        },
        border: '2px solid transparent',
        borderColor: theme.palette.primary.light,
        background: theme.palette.primary.veryLight,
    },
});

interface ByggKontraktListProps {
    byggNummer: string;
}

export const ByggKontraktList: React.FC<ByggKontraktListProps> = ({
    byggNummer,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);
    const params = useAppParams();

    const { selectedClient } = params;
    const kundeNummer = selectedClient?.kundeNummer;

    const defaultLanguage = getDefaultLanguage();
    const danskDeployment = defaultLanguage === 'da';
    const danskOrSvenskDeployment = danskDeployment || defaultLanguage === 'sv';

    // RTK Query
    const {
        data: responseData,
        isLoading: loading,
        error,
    } = useGetByggKontraktQuery(
        {
            byggNummer,
            kundeNummer,
            filterKontraktDetaljerByArealIkkeMedregnet: danskOrSvenskDeployment,
        },
        { skip: !byggNummer },
    );

    const byggKontrakt = responseData?.byggKontrakt;

    // Process list to grouped list (replicating Redux reducer logic)
    const byggKontraktDetaljerGrouped: {
        [key: string]: Array<ByggKontraktItem>;
    } = {};
    if (byggKontrakt) {
        const kontraktDetaljer =
            responseData?.filterKontraktDetaljerByArealIkkeMedregnet
                ? byggKontrakt.kontraktDetaljer.filter(
                      (k) => !(k.arealIkkeMedregnet && k.amount === 0),
                  )
                : byggKontrakt.kontraktDetaljer;

        kontraktDetaljer.forEach((item: ByggKontraktItem) => {
            const group = item.areaTypeName;
            if (!byggKontraktDetaljerGrouped[group]) {
                byggKontraktDetaljerGrouped[group] = [];
            }
            byggKontraktDetaljerGrouped[group].push(item);
        });
    }

    // Local state
    const [expandAll, setExpandAll] = useState(false);
    const [expandGroup, setExpandGroup] = useState<{ [key: string]: boolean }>(
        {},
    );

    const renderArealAndQuantity = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: ByggKontraktItem,
    ) => {
        const { rentalQty, areal, arealUnits } = row;

        if (areal !== '-' && rentalQty > 0) {
            return (
                <>
                    {rentalQty} {t('kontrakt.pcs')} ({`${areal} ${arealUnits}`})
                </>
            );
        } else if (areal !== '-' && rentalQty === 0) {
            // eslint-disable-next-line react/jsx-no-useless-fragment
            return <>{`${areal}${arealUnits}`}</>;
        } else if (areal === '-' && rentalQty > 0) {
            return (
                <>
                    {rentalQty} {t('kontrakt.pcs')}
                </>
            );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderPricePerPlaceAndM2 = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: ByggKontraktItem,
    ) => {
        const { pricePerPlace, pricePerM2, lokalValuta } = row;

        if (typeof pricePerPlace === 'number' && pricePerPlace > 0) {
            return (
                <>
                    <Amount value={pricePerPlace} />
                    &#8239;{lokalValuta}
                    {t('kontrakt.perStk')}
                </>
            );
        }

        if (typeof pricePerM2 === 'number' && pricePerM2 > 0) {
            return (
                <>
                    <Amount value={pricePerM2} />
                    &#8239;{lokalValuta}
                    {t('kontrakt.perM2')}
                </>
            );
        }
        // eslint-disable-next-line react/jsx-no-useless-fragment
        return <></>;
    };

    const renderPricePerYear = <DataType, FilterType>(
        value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        _row: ByggKontraktItem,
    ): JSX.Element => {
        const { lokalValuta } = _row;
        return (
            <>
                <Amount value={value} /> &#8239;{lokalValuta}
                {t('kontrakt.perAar')}
            </>
        );
    };

    const renderContractNumber = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: ByggKontraktItem,
    ): JSX.Element => (
        <>
            {!selectedClient && (
                <>
                    {row.kundeNavn}
                    <br />
                </>
            )}

            {row.kontraktNummer}
            <br />
            {!danskOrSvenskDeployment && row.contractLineName}
        </>
    );

    const renderContractName = <DataType, FilterType>(
        _value: number,
        _column: TableColumnDef<DataType, FilterType>,
        _context: ITableConsumerState<DataType, FilterType>,
        row: ByggKontraktItem,
    ) => (
        // eslint-disable-next-line react/jsx-no-useless-fragment
        <>
            {danskOrSvenskDeployment
                ? row.contractLineName
                : row.rentalobjectName}
        </>
    );

    const handleExpandAll = () => {
        const expandGroupNew: {
            [key: string]: boolean;
        } = {};
        Object.keys(byggKontraktDetaljerGrouped).forEach(
            (group) => (expandGroupNew[group] = !expandAll),
        );

        setExpandGroup(expandGroupNew);
        setExpandAll(!expandAll);
    };

    const handleExpand = (group: string) => () => {
        setExpandGroup((prevExpandGroup) => ({
            ...prevExpandGroup,
            [group]: !prevExpandGroup[group],
        }));
    };

    const getSum = (list: Array<number>) =>
        Math.round(list.reduce((sum: number, val: number) => val + sum, 0));

    const tableDef: Array<TableColumnDef<ByggKontraktItem, unknown>> = [
        new TableColumnDef({
            key: 'kontraktNummer',
            translation: 'kontrakt.kontraktNummer',
            sortable: true,
            align: 'left',
            render: renderContractNumber,
        }),
        new TableColumnDef({
            key: 'rentalobjectName',
            translation: 'kontrakt.navn',
            align: 'left',
            render: renderContractName,
            sortable: true,
        }),
        new TableColumnDef({
            key: 'validFrom',
            translation: 'kontrakt.fra',
            type: 'date',
            sortable: true,
        }),
        ...(!danskDeployment
            ? [
                  new TableColumnDef({
                      key: 'validTo',
                      translation: 'kontrakt.til',
                      type: 'date',
                      sortable: true,
                  }),
              ]
            : []),
        ...(danskOrSvenskDeployment
            ? [
                  new TableColumnDef({
                      key: 'fristOppsigelseLeietaker',
                      translation: 'kontrakt.fristOppsigelseLeietaker',
                      type: 'date',
                      sortable: true,
                  }),
              ]
            : []),
        new TableColumnDef({
            key: '',
            translation: 'kontrakt.antall',
            align: 'right',
            render: renderArealAndQuantity,
        }),
        new TableColumnDef({
            key: 'pricePerM2',
            translation: 'kontrakt.pricePerUnit',
            sortable: true,
            type: 'number',
            render: renderPricePerPlaceAndM2,
        }),
        new TableColumnDef({
            key: 'amount',
            translation: 'kontrakt.prisPerAar',
            align: 'right',
            sortable: true,
            type: 'number',
            render: renderPricePerYear,
        }),
    ];

    return (
        <Loader isLoading={loading}>
            <Heading
                translation="bygg.contractDetails"
                klpHeadingLevel="THREE"
                marginBottom="compact"
            />
            <Conditional
                condition={!error}
                errorComponent={
                    <ErrorNotification
                        title="kontrakt.errorDownload.title"
                        description="kontrakt.errorDownload.description"
                        backTarget={AppRoutes.mainPage()}
                        backTitle="general.goBackToFrontpage"
                    />
                }
            >
                {byggKontrakt && (
                    <ByggKontraktSummary kontrakt={byggKontrakt} />
                )}

                <Conditional
                    condition={byggKontraktDetaljerGrouped && !loading}
                    errorComponent={
                        <Block marginTop="fat">
                            <ErrorNotification
                                title="kontrakt.errorContractNotFound.title"
                                description="kontrakt.errorContractNotFound.description"
                            />
                        </Block>
                    }
                >
                    <Block marginTop="bulky">
                        <Heading
                            klpHeadingLevel="THREE"
                            translation="kontrakt.kontraktAgreementsActive"
                            marginVertical="bulky"
                        />

                        <Block marginBottom="tiny">
                            <Alert severity="info">
                                {t('kontrakt.kontraktAgreementsInfobox')}{' '}
                                <Link
                                    href={ContractManagementRoutes.moduleRoute()}
                                >
                                    {t('contractManagement.title')}
                                </Link>
                            </Alert>
                        </Block>

                        <Block marginBottom="compact">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={expandAll}
                                        onChange={handleExpandAll}
                                        value="expandAll"
                                        color="secondary"
                                    />
                                }
                                label={t('general.showAll')}
                            />
                        </Block>
                        <Block>
                            {Object.entries(
                                byggKontraktDetaljerGrouped || {},
                            ).map(([groupName, groupData]) => {
                                const arealSum = getSum(
                                    groupData
                                        .map(({ areal }) => areal as number)
                                        .filter((a) => !isNaN(a)),
                                );
                                const rentalQtySum = getSum(
                                    groupData.map(({ rentalQty }) => rentalQty),
                                );

                                const uniqueContracts = groupData
                                    .map((item) => item.kontraktNummer)
                                    .filter(
                                        (value, index, self) =>
                                            self.indexOf(value) === index,
                                    );

                                const formattedGroup = groupName?.toLowerCase();

                                return (
                                    <Fragment key={groupName}>
                                        <Accordion
                                            expanded={!!expandGroup[groupName]}
                                            onChange={handleExpand(groupName)}
                                            elevation={0}
                                            className={classes.Accordion}
                                        >
                                            <AccordionSummary
                                                expandIcon={
                                                    <Illustration
                                                        icon={IconName.DOWN}
                                                        klpPalette="secondary"
                                                    />
                                                }
                                                className={
                                                    classes.AccordionSummary
                                                }
                                            >
                                                <Heading
                                                    klpHeadingLevel="SIX"
                                                    translation={`kontrakt.${formattedGroup}`}
                                                />
                                                <Typography
                                                    marginLeft="compact"
                                                    variant="body1"
                                                >
                                                    (
                                                    {false && arealSum > 0 && (
                                                        <>
                                                            {arealSum}
                                                            {t('kontrakt.m2')}
                                                            ,{' '}
                                                        </>
                                                    )}
                                                    {false &&
                                                        rentalQtySum > 0 && (
                                                            <>
                                                                {rentalQtySum}{' '}
                                                                {t(
                                                                    'kontrakt.pcs',
                                                                )}
                                                                ,{' '}
                                                            </>
                                                        )}
                                                    {uniqueContracts?.length}{' '}
                                                    {t('kontrakt.agreements', {
                                                        count: uniqueContracts?.length,
                                                    })}
                                                    , {groupData?.length}{' '}
                                                    {t(
                                                        'kontrakt.agreementRows',
                                                        {
                                                            count: groupData?.length,
                                                        },
                                                    )}
                                                    )
                                                </Typography>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                className={
                                                    classes.AccordionDetails
                                                }
                                            >
                                                <div>
                                                    <Table
                                                        id="ByggKontraktList"
                                                        data={groupData}
                                                        columns={tableDef}
                                                    />
                                                </div>
                                            </AccordionDetails>
                                        </Accordion>
                                    </Fragment>
                                );
                            })}
                        </Block>
                    </Block>
                </Conditional>
            </Conditional>
        </Loader>
    );
};

ByggKontraktList.displayName = 'ByggKontraktList';
